<template>
  <div class="onboarding-surveys" redesign>
    <!-- Header -->
    <div class="local-hero">
      <div class="local-hero-wrapper">
        <h2 class="local-hero-subtitle" v-if="numberOfIncompleteSurveys && !loading">Enrollment Questionnaire ({{ currentSurveyCount }} of {{ numberOfIncompleteSurveys }})</h2>
        <h1 class="local-hero-header">{{ loading ? $gettext('Enrollment Questionnaire') : currentSurveyName }}</h1>
      </div>
    </div>

    <div class="onboarding-surveys-form-container form-container">
      <!-- if loading, show LoadSpinner -->
      <LoadSpinner v-if="loading" :color="`#${organizationDetails.darkBrandColor}`" />
      <!-- else, show page-content -->
      <template v-else>
        <div class="card-shape">
          <SurveyResponseComponent
            v-if="surveyResponse"
            :surveyResponse="surveyResponse"
            :nextStep="nextSurveyName"
            :buttonText="nextSurveyName ? $gettext('Next') : $gettext('Complete Enrollment')"
            @survey-done="onSurveyDone"
          />
        </div>
      </template>
    </div>
  </div>
</template>

<script>
// components
import { LoadSpinner } from '@/components'
import SurveyResponseComponent from '@/components/SurveyResponse'
// service models
import SurveyResponse from '@/services/surveyresponses'
import { mapState } from 'vuex'

export default {
  name: 'RequiredOnboardingSurveys',
  components: {
    LoadSpinner,
    SurveyResponseComponent,
  },
  data() {
    return {
      loading: true, // initial page load
      currentSurveyIndex: -1,
      currentSurveyCount: 1,
      onboardingSurveys: null,
      onboardingSurveysCompleted: null,
      surveyResponse: null,
    }
  },
  created() {
    this.loading = true
  },
  async mounted() {
    this.onboardingSurveys = this.requiredPostRegistrationSurveys
    this.onboardingSurveysCompleted = []
    for(let surveyIndex = 0; surveyIndex < this.onboardingSurveys.length; surveyIndex++) {
      const survey = this.onboardingSurveys[surveyIndex]
      const isComplete = await SurveyResponse.checkIfEmployeeHasCompleted(this.user, survey.surveyName)
      if(this.currentSurveyIndex < 0 && !isComplete) {
        this.currentSurveyIndex = surveyIndex
      }
      this.onboardingSurveysCompleted.push(isComplete)
    }

    await this.getSurveyResponse()
    if(!this.surveyResponse) {
      this.routeHome()
    }
    this.loading = false
  },
  methods: {
    async getSurveyResponse() {
      if(this.currentSurveyIndex < 0)
        return
      const surveyResponse = await SurveyResponse.getOrCreate(this.user, this.onboardingSurveys[this.currentSurveyIndex].surveyName)
      this.surveyResponse = surveyResponse
    },
    async onSurveyDone() {
      this.loading = true
      this.surveyResponse = null
      let nextIncompleteSurveyIndex = this.currentSurveyIndex + 1
      
      if (nextIncompleteSurveyIndex >= this.onboardingSurveys.length) {
        await this.$store.dispatch('refreshCurrentUser')
        this.routeHome()
        return
      }

      for(let surveyIndex = nextIncompleteSurveyIndex; surveyIndex < this.onboardingSurveys.length; surveyIndex++) {
        if(!this.onboardingSurveysCompleted[surveyIndex]) {
          this.currentSurveyIndex = surveyIndex
          this.currentSurveyCount++
          this.surveyResponse = null
          await this.getSurveyResponse()
          this.loading = false
          return
        }
      }
      
      await this.$store.dispatch('refreshCurrentUser')
      this.routeHome()
    },
    routeHome() {
      if(this.organizationDetails.isTcrOrganization) {
        this.$router.push({ name: 'NomineeHome' }) 
        return 
      } else if(this.organizationDetails.isNorthwestern) {
        this.$router.push({ name: 'NorthwesternNomineeHome' })
        return
      }
      this.$router.push({ name: 'Home' })
    },
  },
  computed: {
    ...mapState(['user', 'organizationDetails']),
    requiredPostRegistrationSurveys() {
      if(this.organizationDetails) {
        return this.organizationDetails.organizationSurveys.filter(survey => {
          if(survey.surveyType == 'ONBOARDING' && survey.requiredPostRegistration)
            return survey
        }).sort((a, b) => {
          if(a.order < b.order) {
            return -1
          } else if(a.order > b.order) {
            return 1
          } else {
            return 0
          }
        })
      }

      return []
    },
    currentSurveyName() {
      if(!this.onboardingSurveys || !this.onboardingSurveys.length || this.currentSurveyIndex < 0)
        return "Enrollment Questionnaire"

      return this.onboardingSurveys[this.currentSurveyIndex].surveyName
    },
    nextSurveyName() {
      if(!this.onboardingSurveys || !this.onboardingSurveys.length || this.currentSurveyIndex < 0)
        return ""

      if(this.currentSurveyIndex + 1 >= this.onboardingSurveys.length)
        return ""

      for(let surveyIndex = this.currentSurveyIndex + 1; surveyIndex < this.onboardingSurveys.length; surveyIndex++) {
        if(!this.onboardingSurveysCompleted[surveyIndex])
          return this.onboardingSurveys[surveyIndex].surveyName
      }
    },
    numberOfIncompleteSurveys() {
      if(!this.onboardingSurveysCompleted || !this.onboardingSurveysCompleted.length)
        return null

      return this.onboardingSurveysCompleted.filter(completed => {
        return !completed
      }).length
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/styles/variables';
@import '@/styles/mixins/containers';
@import '@/styles/mixins/responsive';

.onboarding-surveys {
  display: flex;
  flex-direction: column;

  .onboarding-surveys-form-container, .local-hero-wrapper {
    @extend .constrain-tablet-and-up;
  }

  .local-hero {
    display: flex;
    justify-content: center;
  }

  .local-hero-wrapper {
    max-width: 860px;
  }

  .local-hero-header, .local-hero-subtitle {
    text-align: left;
  }
}
</style>
